<template>
  <div class="my-5 mx-12">
    <Slider
      v-model="sliderRange"
      :min="0"
      :max="sliderMax"
      :step="1"
      class="col-span-5 my-4"
      @update:model-value="sliderInput"
      :format-tooltip="tooktipFormat"
    />

    <div class="grid grid-cols-10 gap-8">
      <div class="flex flex-col items-center col-span-4 gap-2">
        <div class="text-base">开始时间</div>
        <div class="grid grid-cols-15 items-center gap-3">
          <NumberField
            v-model="time.startMinute"
            :min="0"
            :max="99"
            class="col-span-3"
            @update:model-value="startCheck"
            :format-options="{ minimumIntegerDigits: 2 }"
          >
            <NumberFieldContent>
              <NumberFieldInput />
            </NumberFieldContent>
          </NumberField>
          <span>分</span>
          <NumberField
            v-model="time.startSecond"
            :min="0"
            :max="59"
            class="col-span-3"
            @update:model-value="startCheck"
            :format-options="{ minimumIntegerDigits: 2 }"
          >
            <NumberFieldContent>
              <NumberFieldInput />
            </NumberFieldContent>
          </NumberField>
          <span>秒</span>
          <NumberField
            v-model="time.startMillisecond"
            :min="0"
            :max="999"
            class="col-span-4"
            @update:model-value="startCheck"
            :format-options="{ minimumIntegerDigits: 3 }"
          >
            <NumberFieldContent>
              <NumberFieldInput />
            </NumberFieldContent>
          </NumberField>
          <span class="col-span-3">毫秒</span>
        </div>
      </div>
      <div ref="reload" class="col-span-2 f-c text-5xl transition-transform duration-1000 ease-in-out" @click="videoReload">
        <SvgIcon :class="[isRotating ? ' animate-rotate360' : '']" name="reload" class="fill-[#1296db]! hover:cursor-pointer"></SvgIcon>
      </div>
      <div class="flex flex-col items-center col-span-4 gap-2">
        <div class="text-base">结束时间</div>
        <div class="grid grid-cols-15 items-center gap-3">
          <NumberField
            v-model="time.endMinute"
            :min="0"
            :max="99"
            class="col-span-3"
            @update:model-value="endCheck"
            :format-options="{ minimumIntegerDigits: 2 }"
          >
            <NumberFieldContent>
              <NumberFieldInput />
            </NumberFieldContent>
          </NumberField>
          <span>分</span>
          <NumberField
            v-model="time.endSecond"
            :min="0"
            :max="59"
            class="col-span-3"
            @update:model-value="endCheck"
            :format-options="{ minimumIntegerDigits: 2 }"
          >
            <NumberFieldContent>
              <NumberFieldInput />
            </NumberFieldContent>
          </NumberField>
          <span>秒</span>
          <NumberField
            v-model="time.endMillisecond"
            :min="0"
            :max="999"
            class="col-span-4"
            @update:model-value="endCheck"
            :format-options="{ minimumIntegerDigits: 3 }"
          >
            <NumberFieldContent>
              <NumberFieldInput />
            </NumberFieldContent>
          </NumberField>
          <span class="col-span-3">毫秒</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Slider } from '~/components/ui/slider';
import { NumberField, NumberFieldContent, NumberFieldInput } from '~/components/ui/number-field';
const sliderMax = ref(10000);
const sliderRange = ref([0, sliderMax.value]);

const duration = ref(0);

const emit = defineEmits(['reload']);

const time = reactive({
  startMinute: 0,
  startSecond: 0,
  startMillisecond: 0,

  endMinute: 0,
  endSecond: 0,
  endMillisecond: 0,
});

//---------------------监听时间滑块变化而改变数字显示------------------------
const sliderInput = (value: number[] | undefined) => {
  if (!value) return;
  const leftValue = value[0];
  let currentTime = duration.value * (leftValue / sliderMax.value);
  time.startMinute = Math.floor(currentTime / 60);
  time.startSecond = Math.floor(currentTime % 60);
  time.startMillisecond = Math.floor((currentTime * 1000) % 1000);

  const rightValue = value[1];
  currentTime = duration.value * (rightValue / sliderMax.value);
  time.endMinute = Math.floor(currentTime / 60);
  time.endSecond = Math.floor(currentTime % 60);
  time.endMillisecond = Math.floor((currentTime * 1000) % 1000);
};
//---------------------防止时间范围出错（数字+大小+小于视频时长+滑块联动）------------------------
const leftTimePos = () => {
  return time.startMinute * 60 + time.startSecond + time.startMillisecond / 1000;
};

const rightTimePos = () => {
  return time.endMinute * 60 + time.endSecond + time.endMillisecond / 1000;
};

// gif开始时间check
const startCheck = () => {
  if (leftTimePos() > duration.value) {
    time.startMinute = 0;
    time.startSecond = 0;
    time.startMillisecond = 0;
  }
  sliderRange.value[0] = Math.floor((leftTimePos() / duration.value) * sliderMax.value);
};

// gif结束时间check
const endCheck = () => {
  if (rightTimePos() > duration.value) {
    time.endMinute = Math.floor(duration.value / 60);
    time.endSecond = Math.floor(duration.value % 60);
    time.endMillisecond = Math.floor((duration.value * 1000) % 1000);
  }
  sliderRange.value[1] = Math.floor((rightTimePos() / duration.value) * sliderMax.value);
};

//---------------------滑块数字显示------------------------
const tooktipFormat = (val: number) => {
  return ((val / sliderMax.value) * duration.value).toFixed(3) + 's';
};

const isRotating = ref(false);
const reload = ref();

const initTime = (videoDuration: number) => {
  duration.value = videoDuration;
  sliderMax.value = videoDuration * 1000;
  time.endMinute = Math.floor(duration.value / 60);
  time.endSecond = Math.floor(duration.value % 60);
  time.endMillisecond = Math.floor((duration.value * 1000) % 1000);

  // 置空时间轴
  sliderRange.value = [0, sliderMax.value];
  sliderInput(sliderRange.value);
};

const videoReload = () => {
  isRotating.value = true;
  setTimeout(() => {
    isRotating.value = false;
  }, 800);
  emit('reload');
};

defineExpose({
  initTime,
  leftTimePos,
  rightTimePos,
});
</script>
